import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import {
	Card,
	CardHeader,
	CardContent,
	CardActions,
	Grid2,
	Button,
	Divider,
	TextField,
	colors,
} from '@mui/material';

import { SetFormState, ChangePassword } from '../../../../actions/SettingsActions';

const schema = {
	oldpassword: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	newpassword: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	confirmnewpassword: {
		presence: { allowEmpty: false, message: 'is required' },
		equality: 'newpassword',
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	saveButton: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const Security = (props) => {
	const { className, ...rest } = props;

	const classes = useStyles();

	const dispatch = useDispatch();

	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.settingsReducer.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		dispatch(SetFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		}));
	}, [formState.values]);

	const handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _onConfirmClick = () => {
		if (formState.isValid) {
			dispatch(ChangePassword(formState.values));
		}
	};

	const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<CardHeader title="Change password" />
			<Divider />
			<CardContent>
				<Grid2 container spacing={3}>
					<Grid2 item size={4}>
						<TextField
							error={hasError('oldpassword')}
							fullWidth
							helperText={
								hasError('oldpassword') ? formState.errors.oldpassword[0] : null
							}
							label="Old password"
							name="oldpassword"
							type="password"
							onChange={handleChange}
							value={formState.values.oldpassword || ''}
							variant="outlined"
						/>
					</Grid2>
					<Grid2 item size={4}>
						<TextField
							error={hasError('newpassword')}
							fullWidth
							helperText={
								hasError('newpassword') ? formState.errors.newpassword[0] : null
							}
							label="New password"
							name="newpassword"
							type="password"
							onChange={handleChange}
							value={formState.values.newpassword || ''}
							variant="outlined"
						/>
					</Grid2>
					<Grid2 item size={4}>
						<TextField
							error={hasError('confirmnewpassword')}
							fullWidth
							helperText={
								hasError('confirmnewpassword') ? formState.errors.confirmnewpassword[0] : null
							}
							label="Confirm new password"
							name="confirmnewpassword"
							type="password"
							onChange={handleChange}
							value={formState.values.confirmnewpassword || ''}
							variant="outlined"
						/>
					</Grid2>
				</Grid2>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					onClick={_onConfirmClick}
					className={classes.saveButton}
					disabled={!formState.isValid}
					variant="contained"
				>
					Save changes
				</Button>
			</CardActions>
		</Card>
	);
};

Security.propTypes = {
	className: PropTypes.string,
};

export default Security;
